import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Music, Upload, Sparkles, X, Film, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface VideoSettings {
  audioBlob: Blob | null;
  audioName: string;
  durationPerSlide: number;
  loops: number;
}

interface VideoSettingsPanelProps {
  onGenerate: (settings: VideoSettings) => void;
  onBack: () => void;
  isGenerating: boolean;
  imageCount: number;
}

const VideoSettingsPanel: React.FC<VideoSettingsPanelProps> = ({
  onGenerate,
  onBack,
  isGenerating,
  imageCount,
}) => {
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioName, setAudioName] = useState("");
  const [durationPerSlide, setDurationPerSlide] = useState(6);
  const [loops, setLoops] = useState(1);
  const [musicPrompt, setMusicPrompt] = useState("");
  const [isGeneratingMusic, setIsGeneratingMusic] = useState(false);
  const [showPromptInput, setShowPromptInput] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAudioBlob(file);
    setAudioName(file.name);
    setShowPromptInput(false);
  };

  const removeAudio = () => {
    setAudioBlob(null);
    setAudioName("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleGenerateMusic = async () => {
    if (!musicPrompt.trim()) {
      toast.error("Please enter a music description first");
      return;
    }

    setIsGeneratingMusic(true);
    try {
      const totalDuration = Math.min(imageCount * durationPerSlide * loops, 30);
      const { data, error } = await supabase.functions.invoke("generate-music", {
        body: { prompt: musicPrompt.trim(), duration: totalDuration },
      });

      if (error) throw error;
      if (!data?.url) throw new Error("No audio URL returned");

      // Fetch the audio and convert to a Blob
      // Use the edge function to proxy the download to avoid CORS issues
      const audioRes = await fetch(data.url, { mode: 'cors' });
      if (!audioRes.ok) {
        // Fallback: try fetching without CORS mode
        const fallbackRes = await fetch(data.url);
        if (!fallbackRes.ok) throw new Error("Failed to download generated audio");
        const blob = await fallbackRes.blob();
        setAudioBlob(new Blob([blob], { type: 'audio/mpeg' }));
        setAudioName(`AI Music: ${musicPrompt.slice(0, 30)}…`);
        setShowPromptInput(false);
        toast.success("AI music generated!");
        return;
      }
      const blob = await audioRes.blob();
      // Ensure the blob has the correct MIME type for MP3
      const typedBlob = new Blob([blob], { type: 'audio/mpeg' });

      setAudioBlob(typedBlob);
      setAudioName(`AI Music: ${musicPrompt.slice(0, 30)}…`);
      setShowPromptInput(false);
      toast.success("AI music generated!");
    } catch (err) {
      console.error("Music generation error:", err);
      toast.error(err instanceof Error ? err.message : "Failed to generate music");
    } finally {
      setIsGeneratingMusic(false);
    }
  };

  const totalDuration = imageCount * durationPerSlide * loops;
  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
  };

  return (
    <div className="w-full max-w-md mx-auto space-y-6">
      <div className="text-center mb-2">
        <Film className="h-10 w-10 text-primary mx-auto mb-3" />
        <h3 className="text-lg font-medium text-foreground">Video Settings</h3>
        <p className="text-muted-foreground text-sm mt-1">
          Customize your slideshow before creating
        </p>
      </div>

      {/* Music */}
      <div className="space-y-3">
        <Label className="text-sm font-medium text-foreground flex items-center gap-2">
          <Music className="h-4 w-4" /> Background Music
        </Label>

        {audioName ? (
          <div className="flex items-center gap-2 rounded-lg border border-border bg-secondary/50 px-3 py-2">
            <Music className="h-4 w-4 text-primary shrink-0" />
            <span className="text-sm text-foreground truncate flex-1">{audioName}</span>
            <button onClick={removeAudio} className="text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <>
            <div className="flex gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*"
                className="hidden"
                onChange={handleFileUpload}
              />
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => {
                  setShowPromptInput(false);
                  fileInputRef.current?.click();
                }}
                disabled={isGeneratingMusic}
              >
                <Upload className="mr-2 h-4 w-4" /> Upload Audio
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => setShowPromptInput((v) => !v)}
                disabled={isGeneratingMusic}
              >
                <Sparkles className="mr-2 h-4 w-4" /> AI Music
              </Button>
            </div>

            {showPromptInput && (
              <div className="space-y-2">
                <Input
                  placeholder="e.g. calming piano, ambient meditation…"
                  value={musicPrompt}
                  onChange={(e) => setMusicPrompt(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleGenerateMusic()}
                  disabled={isGeneratingMusic}
                  className="text-sm"
                />
                <Button
                  size="sm"
                  className="w-full cosmic-button"
                  onClick={handleGenerateMusic}
                  disabled={isGeneratingMusic || !musicPrompt.trim()}
                >
                  {isGeneratingMusic ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Generating music…
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-4 w-4" />
                      Generate Music
                    </>
                  )}
                </Button>
                <p className="text-xs text-muted-foreground">
                  Music will be trimmed to match your video length (max 30s).
                </p>
              </div>
            )}
          </>
        )}

        <p className="text-xs text-muted-foreground">Optional. MP3 or WAV recommended for uploads.</p>
      </div>

      {/* Duration per slide */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium text-foreground">Time per Image</Label>
          <span className="text-sm text-muted-foreground">{durationPerSlide}s</span>
        </div>
        <Slider
          min={3}
          max={15}
          step={1}
          value={[durationPerSlide]}
          onValueChange={([v]) => setDurationPerSlide(v)}
        />
      </div>

      {/* Loops */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium text-foreground">Number of Loops</Label>
          <span className="text-sm text-muted-foreground">{loops}×</span>
        </div>
        <Slider
          min={1}
          max={5}
          step={1}
          value={[loops]}
          onValueChange={([v]) => setLoops(v)}
        />
      </div>

      {/* Summary */}
      <p className="text-center text-xs text-muted-foreground">
        {imageCount} images × {durationPerSlide}s × {loops} loop{loops > 1 ? "s" : ""} ={" "}
        <span className="text-foreground font-medium">{formatTime(totalDuration)}</span> total
      </p>

      {/* Actions */}
      <div className="flex gap-3 pt-2">
        <Button variant="outline" onClick={onBack} disabled={isGenerating || isGeneratingMusic} className="flex-1">
          Back
        </Button>
        <Button
          className="cosmic-button flex-1"
          onClick={() => onGenerate({ audioBlob, audioName, durationPerSlide, loops })}
          disabled={isGenerating || isGeneratingMusic}
        >
          {isGenerating ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Film className="mr-2 h-4 w-4" />
          )}
          {isGenerating ? "Creating…" : "Create Video"}
        </Button>
      </div>
    </div>
  );
};

export default VideoSettingsPanel;
